// src/utils/mixPreviewWeb.ts
import { getAccessToken } from "../api/axios";

const API_BASE = process.env.REACT_APP_API_BASE_URL as string;

if (!API_BASE) {
  throw new Error("REACT_APP_API_BASE_URL is not defined");
}

const CSRF_COOKIE_NAME = process.env.REACT_APP_CSRF_COOKIE_NAME || "csrf_token";
const CSRF_HEADER_NAME = "x-csrf-token";

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&") + "=([^;]*)")
  );
  return m ? decodeURIComponent(m[1]) : null;
}

export async function mixPreviewWeb(opts: {
  file: File;
  // visual
  filterId?: string;
  // music
  trackId?: string;
  volume?: number;
  offsetSec?: number;
  mixOriginal?: boolean;

  // transport
  /**
   * Optional override. If not provided, we will use the in-memory access token
   * from axios.ts (getAccessToken()).
   */
  token?: string | null;

 /**
 * e.g. "https://api.example.com" or ".../api" (we strip "/api").
 * Defaults to REACT_APP_API_BASE_URL.
 */
  apiBase?: string;

  timeoutMs?: number;

  /**
   * If true, we send cookies (refresh/csrf) cross-origin as well.
   * Recommended when your backend sets SameSite=None cookies in prod.
   */
  useCookies?: boolean;
}): Promise<string> {
  const {
    file,
    filterId = "none",
    trackId,
    volume,
    offsetSec,
    mixOriginal = true,

    token,
    apiBase = API_BASE,
    useCookies = true, // ✅ default to true because you rely on refresh/csrf cookies
    timeoutMs,
  } = opts;

  if (!file || !(file instanceof File)) {
    throw new Error("mixPreviewWeb: a valid File is required");
  }
  if (!file.type?.startsWith("video/")) {
    throw new Error(`mixPreviewWeb: expected a video file, got "${file.type || "unknown"}"`);
  }

  // Normalize base: strip trailing "/api" and trailing "/"
  const base = apiBase.replace(/\/api\/?$/, "").replace(/\/$/, "");

  const form = new FormData();
  form.append("video", file, file.name);

  // Visual filter
  if (filterId && filterId !== "none") {
    form.append("filterId", filterId);
  }

  // Music (when provided)
  if (trackId) {
    form.append("trackId", trackId);
    if (typeof volume === "number") form.append("volume", String(volume));
    if (typeof offsetSec === "number") form.append("offset", String(Math.max(0, offsetSec)));
    form.append("mixOriginal", String(!!mixOriginal));
  } else {
    // No music: allow fast path only if no visual filter
    if (!filterId || filterId === "none") {
      form.append("audioOnly", "1"); // hint server it can -c:v copy
    }
    form.append("mixOriginal", String(!!mixOriginal));
    if (!mixOriginal) form.append("muteOriginal", "1");
  }

  // Smart timeout: ~1s per MB, min 60s, max 5 min
  const sizeMB = file.size / (1024 * 1024);
  const smartTimeout =
    typeof timeoutMs === "number"
      ? timeoutMs
      : Math.max(60_000, Math.min(300_000, Math.round(sizeMB) * 1_000));

  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), smartTimeout);

  try {
    // ✅ Use passed token OR in-memory access token from axios.ts
    const bearer = token ?? getAccessToken();

    // ✅ CSRF header (backend expects x-csrf-token for unsafe requests if you enforce it)
    const csrf = getCookie(CSRF_COOKIE_NAME);

    const headers: Record<string, string> = {};
    if (bearer) headers.Authorization = `Bearer ${bearer}`;
    if (csrf) headers[CSRF_HEADER_NAME] = csrf;

    const res = await fetch(`${base}/api/videopreview`, {
      method: "POST",
      headers: Object.keys(headers).length ? headers : undefined,
      body: form,
      signal: ac.signal,
      credentials: useCookies ? "include" : "same-origin",
    });

    if (!res.ok) {
      let msg = `videopreview failed: ${res.status}`;
      try {
        const ct = res.headers.get("content-type") || "";
        if (ct.includes("application/json")) {
          const j = await res.json();
          if (j?.detail) msg += ` — ${j.detail}`;
          else if (j?.error) msg += ` — ${j.error}`;
          else if (j?.message) msg += ` — ${j.message}`;
        } else {
          const t = await res.text();
          if (t) msg += ` — ${t.slice(0, 500)}`;
        }
      } catch {
        /* ignore parse errors */
      }
      throw new Error(msg);
    }

    const j = await res.json();
    if (!j?.previewUrl) throw new Error("No previewUrl returned");
    return j.previewUrl as string;
  } catch (err: any) {
    if (err?.name === "AbortError") {
      throw new Error(
        `Preview timed out after ${Math.round(smartTimeout / 1000)}s — try a smaller file, remove heavy filters, or increase timeoutMs.`
      );
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}
